import is from '@sindresorhus/is';
import noop from 'lodash/noop';
import { useCallback, useSyncExternalStore } from 'react';

import type { ChatAgentRegistry } from './chat-agent-registry';
import type { ChatAgentSnapshot } from './thread-agent-session';

/**
 * The thread's agent state, re-rendering at most once per animation frame: every streamed token
 * replaces the agent's message list, so rendering per event would re-render per token.
 */
export const useChatAgent = (registry: ChatAgentRegistry, threadId: string | null): ChatAgentSnapshot => {
    const subscribe = useCallback(
        (notify: () => void): (() => void) => {
            if (is.null(threadId)) return noop;
            let frame: number | null = null;
            const unsubscribe = registry.subscribe(threadId, () => {
                if (!is.null(frame)) return;
                frame = requestAnimationFrame(() => {
                    frame = null;
                    notify();
                });
            });
            return () => {
                if (!is.null(frame)) cancelAnimationFrame(frame);
                unsubscribe();
            };
        },
        [registry, threadId],
    );
    const getSnapshot = useCallback(() => registry.getSnapshot(threadId), [registry, threadId]);

    return useSyncExternalStore(subscribe, getSnapshot);
};
